import React from 'react';
import {reactCreateMemo} from '@wix/devzai-utils-react';
import {classes, style} from './doppe-viewer-payments-disabled-modal.st.css';
import {useDoppeViewerLocales} from '../../client/doppe-viewer-locales/doppe-viewer-locales';
import type {DoppePremiumFeaturesStatus} from '../../client-server-common/doppe-premium/doppe-premium';
import {doppePremiumFeatureIsDisabled} from '../../client-server-common/doppe-premium/doppe-premium';
import {DoppePremiumFeatures} from '../../client-server-common/doppe-premium/doppe-premium-features';
import {DoppeViewerContentModal} from '../doppe-viewer-content-modal/doppe-viewer-content-modal';
import {DoppeViewerModalPanelLayout} from '../doppe-viewer-modal-panel/doppe-viewer-modal-panel';

/**
 * Modal shown when the page owner cannot accept online payments via Wix eCom.
 * Self-contained panel — wraps the body content in a `DoppeViewerContentModal`
 * with a header close button and the standard mobile-bottom-drawer / desktop-inner layouts.
 *
 * Render via `modalsController.showInnerModal({ content: ({modalController}) =>
 *     <DoppeViewerPaymentsDisabledModal closeCallback={() => modalController.closeWindow()} /> })`.
 */
export const DoppeViewerPaymentsDisabledModal = reactCreateMemo(
    function DoppeViewerPaymentsDisabledModal(props: DoppeViewerPaymentsDisabledModal.Props) {
        const {closeCallback} = props;
        const viewerLocales = useDoppeViewerLocales();
        const title = viewerLocales['paymentsDisabled.title'];
        const body = viewerLocales['paymentsDisabled.body'];

        return (
            <DoppeViewerContentModal
                closeCallback={closeCallback}
                mobileLayout={DoppeViewerModalPanelLayout.BottomDrawer}
                desktopLayout={DoppeViewerModalPanelLayout.Inner}
                content={() => ({
                    render: () => (
                        <div className={style(classes.root, {})}>
                            <h2 className={classes.title}>{title}</h2>
                            <p className={classes.body}>{body}</p>
                        </div>
                    ),
                    dependencies: [],
                })}
                showHeader={true}
                showHeaderAsCloseButton={true}
                title={title}
            />
        );
    }
);

export namespace DoppeViewerPaymentsDisabledModal {
    export interface Props {
        closeCallback: () => void;
    }
}

/**
 * Returns true when the page owner cannot accept online payments via Wix eCom.
 * Callers should open the payments-disabled modal and bail out of the checkout flow
 * before any checkout API call when this returns true.
 */
export function doppeViewerPaymentsDisabledIsBlocked(
    premiumFeaturesStatus: DoppePremiumFeaturesStatus | null
): boolean {
    return doppePremiumFeatureIsDisabled(
        premiumFeaturesStatus,
        DoppePremiumFeatures.hopp_connect_payments_by_wix_on_hopp_met,
    );
}
